import React, {
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import t from 'react-translate';
import uuid from 'react-uuid';
import { useDispatch } from 'react-redux';
import { css } from '@emotion/react';

// redux
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';

import NvTextArea from 'shared/components/inputs/nv-text-area';
import ClickableContainer from 'components/clickable-container';
import NvIcon from 'shared/components/nv-icon';
import { danger, gray3, primary } from 'styles/global_defaults/colors';
import { config } from '../../../../../config/pendo.config.json';
import AiConversationContext,
{
  MessageStatus,
  Role,
} from './context';
import streamAIResponse from './stream-ai-response';

const INACTIVITY_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

const ChatBar = () => {
  const {
    messages,
    setMessages,
    sessionData,
    isTimeout,
    handleExitChat,
    abortControllerRef,
    handleChunk,
    handleError,
  } = useContext(AiConversationContext);

  const dispatch = useDispatch();
  const [message, setMessage] = useState('');
  const inactivityTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const lastMessage = messages[messages.length - 1];

  /**
   *restrict user to send message if the last message is still pending
   *or if the last message is from learner and not failed (to enforce turn-taking)
   *or if there are no messages yet (to enforce starting with AI message)
   *or if the message input is empty
   *or if the session is timed out due to inactivity
   */
  const isSentMessageDisabled = lastMessage?.status === MessageStatus.PENDING
    || (lastMessage?.role === Role.LEARNER && lastMessage?.status !== MessageStatus.FAILED)
    || messages.length === 0
    || !message.trim()
    || isTimeout;

  const styles = css`
    .chat-box {
      border-color: ${isSentMessageDisabled ? gray3 : primary} !important;
      .send-button {
        color: ${isSentMessageDisabled ? gray3 : primary} !important;
      }
    }
    .send-message-textarea {
      width: 90%;
      textarea {
        border: none;
        background-color: transparent;
      }
    }
    .send-button {
      width: 10%;
    }
`;

  useEffect(() => {
    // Clear any existing timeout first to prevent multiple timeouts
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
      inactivityTimeoutRef.current = null;
    }

    // Start a timeout to show an alert if there's no activity for 5 minutes from learner.
    if ((lastMessage?.status === MessageStatus.SENT && lastMessage?.role === Role.AI)
        || lastMessage?.status === MessageStatus.FAILED) {
      inactivityTimeoutRef.current = setTimeout(() => {
        dispatch(addAlertMessage({
          type: AlertMessageType.WARNING,
          header: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.SESSION_ENDED(),
          message: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.INACTIVE_SESSION(),
          customColor: danger,
          closeOnNavigate: true,
          duration: Infinity,
        }));
        // Skip confirmation dialog for timeout since user is inactive
        handleExitChat(true);
      }, INACTIVITY_TIMEOUT_MS);
    }

    // Cleanup on unmount
    return () => {
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
        inactivityTimeoutRef.current = null;
      }
    };
  }, [lastMessage, dispatch, handleExitChat]);

  const handleSendMessage = () => {
    if (isSentMessageDisabled) return;

    if (!sessionData.aiApiEndpoint || !sessionData.jwtToken) {
      dispatch(addAlertMessage({
        type: AlertMessageType.WARNING,
        header: t.FORM.OOPS(),
        message: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.SOMETHING_WENT_WRONG(),
        customColor: danger,
      }));
      return;
    }

    setMessages(prevMessages => [
      ...prevMessages,
      {
        id: uuid(),
        content: message,
        role: Role.LEARNER,
        createdAt: new Date(),
        status: MessageStatus.PENDING,
      },
    ]);
    streamAIResponse({
      aiApiEndpoint: sessionData.aiApiEndpoint,
      token: sessionData.jwtToken,
      userMessage: message,
      onChunk: handleChunk,
      onError: handleError,
      abortSignal: abortControllerRef.current?.signal,
    }).catch((_error) => {
      // Catch the re-thrown error from streamAIResponse to prevent unhandled promise rejection.
      // handleError was already called by onError callback which shows alert and marks message as FAILED.
    });
    setMessage('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !isSentMessageDisabled) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div css={styles} className='w-100 align-items-center mt-auto py-4'>
      <div className='d-flex border-bottom pb-4 bg-white chat-box'>
        <NvTextArea
          className='send-message-textarea'
          placeholder={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.SEND_MESSAGE_PLACEHOLDER()}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isTimeout}
        />
        <ClickableContainer
          onClick={handleSendMessage}
          className='send-button text-gray-4'
          aria-label={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.SEND_MESSAGE()}
          disabled={isSentMessageDisabled}
          data-qa={config.pendo.activities.aiRolePlay.conversationModal.sendMessage}
        >
          <NvIcon
            icon='send-message'
            size='small'
            className='d-flex align-items-center mr-4 ml-auto'
          />
        </ClickableContainer>
      </div>
    </div>
  );
};

export default ChatBar;
